import Dropzone from "react-dropzone";
import upload from "../../assets/icons/darkIcons/upload.svg";
import {
  deleteDocumentByCustomer,
  getDocumentByCustomer,
  getDocumentsByCustomer,
  postDocuments,
} from "../../api/customerFile";
import { useCrmStore } from "../../stores/crm";
import { showToastError, showToastSuccess } from "../../services/toast";
import { useEffect, useState } from "react";
import { normalizeFileName } from "../../services/function";
import eye from "../../assets/icons/eye.svg";
import trash from "../../assets/icons/whiteIcons/trash.svg";
import download from "../../assets/icons/darkIcons/download.svg";
import { ModalButton, ModalShell } from "../ui/modals/ModalShell";

const DocumentsDeposit = ({ idCustomer, closeModal }: any) => {
  const { documents, setDocuments } = useCrmStore();
  const [update, setUpdate] = useState(false);

  useEffect(() => {
    getDocumentsByCustomer(idCustomer)
      .then((res) => {
        setDocuments(res.data);
      })
      .catch(() => {
        showToastError(
          "Une erreur est survenue lors de la récupération des documents"
        );
      });
  }, [update]);

  const handleDrop = (acceptedFiles: File[]) => {
    const uploadPromises = acceptedFiles.map((file) => {
      const formData = new FormData();
      const normalizedFileName = normalizeFileName(file.name);
      formData.append("file", file, normalizedFileName); // Ajoute le fichier directement

      return postDocuments(idCustomer, formData);
    });
    
    Promise.all(uploadPromises)
      .then(() => {
        setUpdate((prev) => !prev);
      })
      .catch(() => {
        showToastError("Une erreur est survenue lors de l'envoi du document");
      });
  };

  const handleViewDoc = (idDoc: number) => {
    getDocumentByCustomer(idCustomer, idDoc)
      .then((response) => {
        const blob = new Blob([response.data], { type: response.data.type });
        const url = window.URL.createObjectURL(blob);

        // Ouvrir le document dans un nouvel onglet
        window.open(url, "_blank");
      })
      .catch(() => {
        showToastError("Erreur lors du téléchargement du fichier");
      });
  };

  const handleDownloadDoc = (idDoc: number, name: string) => {
    getDocumentByCustomer(idCustomer, idDoc)
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", name);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })
      .catch(() => {
        showToastError("Erreur lors du téléchargement du fichier");
      });
  };

  const handleDeleteDocument = (idDoc: number) => {
    deleteDocumentByCustomer(idCustomer, idDoc)
      .then(() => {
        showToastSuccess("Le document a été supprimé avec succès");
        setUpdate((prev) => !prev);
      })
      .catch(() => {
        showToastError("Erreur lors de la suppression du document");
      });
  };

  return (
    <ModalShell
      title="Dépot de documents"
      subtitle="Vous trouverez ci-dessous les documents"
      size="lg"
      onClose={() => closeModal()}
      footer={
        <>
          <ModalButton variant="secondary" onClick={() => closeModal()}>
            Annuler
          </ModalButton>
          <ModalButton
            variant="primary"
            type="submit"
            onClick={() => {
              closeModal();
              showToastSuccess("Les documents ont été envoyés avec succès");
            }}
          >
            Déposer
          </ModalButton>
        </>
      }
    >
      {/* Dropzone */}
      <Dropzone onDrop={handleDrop}>
        {({ getRootProps, getInputProps }) => (
          <div
            {...getRootProps()}
            className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center transition-colors hover:border-indigo-300 hover:bg-indigo-50/50"
          >
            <input {...getInputProps()} />
            <img src={upload} alt="upload" className="size-6 opacity-60" />
            <p className="font-Nunito text-sm text-slate-600">
              Veuillez déposer vos documents ici
            </p>
          </div>
        )}
      </Dropzone>

      {/* Tableau des documents */}
      <div className="mt-4 overflow-hidden rounded-xl border border-slate-200">
        <div className="hidden border-b border-slate-100 bg-slate-50 px-4 py-2.5 sm:flex">
          <p className="w-1/2 text-[11px] font-bold tracking-wider text-slate-500 uppercase">
            Nom du document
          </p>
          <p className="w-[15%] text-[11px] font-bold tracking-wider text-slate-500 uppercase">
            Type
          </p>
          <p className="w-[20%] text-[11px] font-bold tracking-wider text-slate-500 uppercase">
            Date de dépot
          </p>
          <p className="w-[15%]" />
        </div>
        <div className="max-h-64 divide-y divide-slate-100 overflow-y-auto">
          {documents?.length > 0 ? (
            documents?.map((doc) => {
              return (
                <div
                  key={doc.id}
                  className="flex flex-col gap-2 px-4 py-2.5 transition-colors hover:bg-slate-50 sm:flex-row sm:items-center sm:gap-0"
                >
                  <p className="min-w-0 truncate font-Nunito text-sm font-semibold text-slate-800 sm:w-1/2 sm:pr-3 sm:font-normal">
                    {doc.documentFileName}
                  </p>
                  <p className="font-Nunito text-sm text-slate-600 sm:w-[15%]">
                    {doc.type}
                  </p>
                  <p className="font-Nunito text-sm text-slate-600 tabular-nums sm:w-[20%]">
                    {doc.date}
                  </p>
                  <div className="flex items-center gap-1 sm:w-[15%] sm:justify-end">
                    <button
                      className="inline-flex size-9 cursor-pointer items-center justify-center rounded-xl text-slate-400 transition-colors hover:bg-indigo-50 hover:text-dca-blue"
                      title="Consulter"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewDoc(doc.id);
                      }}
                    >
                      <img src={eye} alt="eye" className="size-4" />
                    </button>
                    <button
                      className="inline-flex size-9 cursor-pointer items-center justify-center rounded-xl text-slate-400 transition-colors hover:bg-indigo-50 hover:text-dca-blue"
                      title="Télécharger"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadDoc(doc.id, doc.name);
                      }}
                    >
                      <img src={download} alt="telecharger" className="size-4" />
                    </button>
                    <button
                      className="inline-flex size-9 cursor-pointer items-center justify-center rounded-xl bg-red-600 transition-colors hover:bg-red-700"
                      title="Supprimer"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleDeleteDocument(doc.id);
                      }}
                    >
                      <img src={trash} alt="poubelle" className="size-4" />
                    </button>
                  </div>
                </div>
              );
            })
          ) : (
            <p className="px-4 py-8 text-center font-Nunito text-sm text-slate-400">
              Aucun document disponible
            </p>
          )}
        </div>
      </div>
    </ModalShell>
  );
};

export default DocumentsDeposit;
